/**
 * Protected Route Component
 *
 * Handles authentication-based route protection with loading states
 * and automatic redirects for unauthenticated users.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { captureReturnTo } from '@/hooks/auth/useDeepLinkReturnTo';
import { usePortalHome } from '@/hooks/portal/usePortalHome';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  fallback?: ReactNode;
}

export function ProtectedRoute({
  children,
  redirectTo = '/signin',
  fallback
}: ProtectedRouteProps) {
  const { user, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  // Show loading state while auth is being determined
  if (!isLoaded) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Loading...</h3>
              <p className="text-sm text-muted-foreground">
                Checking authentication status
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated. Capture the original path
  // for deep-link survival across the Auth0 round-trip — both via
  // sessionStorage (survives the redirect) and via router state (works
  // for the immediate signin → callback hop on the same tab).
  if (!isSignedIn || !user) {
    captureReturnTo(`${location.pathname}${location.search}`);
    return (
      <Navigate
        to={redirectTo}
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }

  // Render protected content
  return <>{children}</>;
}

// ============================================================================
// Public Route Component (for auth pages)
// ============================================================================

interface PublicRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

export function PublicRoute({
  children,
  redirectTo
}: PublicRouteProps) {
  const { isLoaded, isSignedIn, isAdmin, isPortalUser } = useAuth();
  const location = useLocation();
  const portalHome = usePortalHome();

  // Show loading state while auth is being determined
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Loading...</h3>
              <p className="text-sm text-muted-foreground">
                Checking authentication status
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Redirect authenticated users to the appropriate dashboard
  if (isSignedIn) {
    const from = (location.state as any)?.from;
    if (from) {
      return <Navigate to={from} replace />;
    }
    const defaultRedirect = redirectTo || (isAdmin ? '/dashboard' : isPortalUser ? portalHome : '/portal/onboarding');
    return <Navigate to={defaultRedirect} replace />;
  }

  // Render public content (sign-in page)
  return <>{children}</>;
}
